import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import VisitDetail from './VisitDetail'
import { FaAngleDown, FaAngleUp } from 'react-icons/fa'
import CustomChip from '../../../shared/CustomChip'

const MultipleVisitsCard = ({
  visitsData,
  getVisitDataPayload,
  slotData,
  visitCategory,
  useVisitCategoryFromVisit,
  getOrdersPayload,
  order,
  orderData,
  visitColor
}) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  if (!visitsData || !Array.isArray(visitsData) || visitsData.length === 0) {
    return (
      <div className='flex flex-col w-full items-center justify-center min-h-[80px] bg-gray-50/50 rounded-md border border-dashed border-gray-200'>
        <p className='text-[13px] font-medium text-gray-400 uppercase tracking-wider'>{t('No visits available')}</p>
      </div>
    )
  }

  const summaryView = (
    <div className='flex flex-col w-full bg-white'>
      <div
        className={`flex justify-between items-center w-full px-4 py-3 cursor-pointer hover:bg-opacity-90 transition-all group ${visitColor}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className='flex items-center gap-3'>
          <div className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center text-gray-700 font-semibold text-sm">
            {visitsData.length}
          </div>
          <div className="flex flex-col">
            <p className='text-[14px] font-semibold text-gray-700 uppercase tracking-tight'>{t('Multiple Visits')}</p>
            <p className='text-[11px] font-medium text-gray-500 uppercase'>
              {visitsData.length} {visitsData.length === 1 ? t('Visit') : t('Visits')} {t('at')} {slotData?.time}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex -space-x-2 overflow-hidden">
            {visitsData.slice(0, 3).map((visit, index) => (
              <div
                key={index}
                className="inline-block h-6 w-6 rounded-full ring-2 ring-white bg-gray-100/80 flex items-center justify-center text-[10px] font-semibold text-gray-600 border border-gray-200"
                title={visit.patient?.full_name || visit.participant}
              >
                {(visit.patient?.full_name || visit.participant || "P")?.[0]}
              </div>
            ))}
            {visitsData.length > 3 && (
              <div className="inline-block h-6 w-6 rounded-full ring-2 ring-white bg-primary text-[10px] font-semibold text-white flex items-center justify-center border border-primary-200">
                +{visitsData.length - 3}
              </div>
            )}
          </div>

          <button className='p-1.5 text-gray-500 bg-white border border-gray-200 rounded-[6px] hover:bg-gray-50 hover:border-gray-300 transition-all  focus:outline-none'>
            {isExpanded ? <FaAngleUp size={16} /> : <FaAngleDown size={16} />}
          </button>
        </div>
      </div>

      <div className='flex items-center justify-between px-4 py-2 border-t border-gray-100 bg-gray-50/30'>
        <div className='flex items-center gap-2 overflow-hidden'>
          <span className="text-[10px] font-semibold text-gray-400 uppercase whitespace-nowrap">{t('Patients')}:</span>
          <div className='flex gap-1.5 flex-nowrap overflow-hidden'>
            {visitsData.slice(0, 2).map((visit, index) => (
              <span key={index} className="text-[13px] font-bold text-gray-700 truncate max-w-[120px]">
                {visit.patient?.full_name || visit.participant}{index < Math.min(visitsData.length, 2) - 1 ? ',' : ''}
              </span>
            ))}
            {visitsData.length > 2 && (
              <span className="text-[11px] font-semibold text-primary-600">
                +{visitsData.length - 2} {t("more")}
              </span>
            )}
          </div>
        </div>

        <button
          className='text-[10px] font-semibold uppercase text-primary hover:text-primary-dark transition-colors bg-white border border-gray-100 rounded-[6px] px-2 py-1  hover:bg-gray-50'
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(true);
          }}
        >
          {t('View All Details')}
        </button>
      </div>
    </div >
  )

  const expandedView = (
    <div className='flex flex-col w-full bg-white'>
      <div
        className={`flex justify-between items-center w-full px-4 py-3 cursor-pointer border-b border-gray-200 hover:bg-opacity-90 transition-all ${visitColor}`}
        onClick={() => setIsExpanded(false)}
      >
        <div className='flex items-center gap-3'>
          <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold text-sm">
            {visitsData.length}
          </div>
          <div className="flex flex-col">
            <p className='text-[14px] font-semibold text-gray-700 uppercase tracking-tight'>{t('Multiple Visits')}</p>
            <p className='text-[11px] font-medium text-gray-600 uppercase'>{t('Collapse and return to summary')}</p>
          </div>
        </div>
        <button className='p-1.5 text-gray-500 bg-white border border-gray-200 rounded-[6px] hover:bg-gray-50 hover:border-gray-300 transition-all  focus:outline-none'>
          <FaAngleUp size={16} />
        </button>
      </div>

      <div className='flex flex-col w-full p-2 gap-2 bg-gray-50/50'>
        {visitsData.map((visit, index) => (
          <div
            key={index}
            className="bg-white rounded-md  border border-gray-200 overflow-hidden"
          >
            <VisitDetail
              order={order}
              orderData={orderData}
              getOrdersPayload={getOrdersPayload}
              visitData={visit}
              getVisitDataPayload={getVisitDataPayload}
              slotData={slotData}
              visitCategory={
                useVisitCategoryFromVisit
                  ? visit?.visit_category
                  : visitCategory
              }
            />
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <div className="w-full transition-all duration-300 ease-in-out">
      {isExpanded ? expandedView : summaryView}
    </div>
  )
}

export default MultipleVisitsCard
